import { Button, Modal, Result, Spin } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import gql from 'graphql-tag';
import dayjs from 'dayjs';
import { createRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import YAML from 'yaml'; // migrate to js-yaml

import AC from '../AccessControl';
import cmsFactory from '../cmsFactory';
import { CREATE_ASSIGNMENT, CREATE_ASSIGNMENT_PLAYLIST } from '../../helpers/gqlMutations/assignments';
import * as ENTITY_QUERIES from '../../helpers/gqlQueries/ui/context';
import { AssignmentsContextProvider } from '../../lib/AssignmentsContext';
import { useApolloClient, useMutation, useRerender, useSession } from '../../lib/hooks';
const STORE_KEY = 'AssignmentsPlaylist';
const Klass = data => null;
const StudentID = data => null;
const Task = async (data, {
  modulesIndex
}) => {
  try {
    const [moduleName, moduleKey, moduleLang] = String(data).split(':');
    const contentID = `${moduleName}:${moduleKey}`;
    const moduleData = modulesIndex[contentID];
    const language = moduleData.language !== 'en_es' ? moduleData.language : ['any', 'en', 'es'].includes(moduleLang) ? moduleLang : moduleData.language;
    const name = language === 'es' ? moduleData.spanishName : moduleData.name;
    const instructions = language === 'es' ? moduleData.spanishLearningObjectiveHTML : moduleData.learningObjectiveHTML;
    const {
      previewURL,
      contentType
    } = moduleData;
    return {
      contentID,
      name,
      instructions,
      language,
      previewURL,
      contentType
    };
  } catch {
    return null;
  }
};
const Moment = data => {
  const dateString = String(data);
  const v = dayjs(dateString);
  return dateString && v.isValid() ? v : null;
};
const GradeID = (data, {
  apolloClient
}) => apolloClient.query({
  query: gql`query UI_GET_GRADE($id: GradeLevel!){ gradeById(id: $id){ id } }`,
  variables: {
    id: String(data)
  }
}).then(r => r.data?.gradeById.id, () => null);
const AgeGroup = (data, {
  apolloClient
}) => apolloClient.query({
  query: gql`query UI_GET_AGEGROUP($id: String!){ ageGroupById(id: $id){ id } }`,
  variables: {
    id: String(data)
  }
}).then(r => r.data?.ageGroupById.id, () => null);
const Entity = async (data, {
  apolloClient
}) => {
  try {
    const [typename, id, type] = String(data).split(/[#:]/);
    if (typename === 'District' && !['DISTRICT', 'PARTNER', 'COMMUNITY'].includes(type)) {
      throw new Error('wrong type');
    }
    const response = await apolloClient.query({
      query: ENTITY_QUERIES[`UI_GET_${typename.toUpperCase()}_CONTEXT`],
      variables: {
        [`${typename.toLowerCase()}Id`]: id
      }
    });
    const respData = response.data[`${typename.toLowerCase()}ById`];
    return respData && {
      type,
      ...respData
    };
  } catch {
    return null;
  }
};
const ArrayOf = Type => async (data, addons) => !Array.isArray(data) ? null : (await Promise.all(data.map(datum => Type(datum, addons)))).filter(Boolean);
const PLAYLIST_DEFAULTS_CONFIG = {
  label: ['My Playlist', String],
  klass: [null, Klass],
  studentIDs: [[], ArrayOf(StudentID)],
  entities: [[], ArrayOf(Entity)],
  grades: [[], ArrayOf(GradeID)],
  ages: [[], ArrayOf(AgeGroup)]
};
const PLAYLIST_DEFAULTS = Object.fromEntries(Object.entries(PLAYLIST_DEFAULTS_CONFIG).map(([k, [v]]) => [k, v]));
const ASSIGNMENT_DEFAULTS_CONFIG = {
  label: ['New Assignment', String],
  startDate: [null, Moment],
  endDate: [null, Moment],
  tasks: [[], ArrayOf(Task)],
  instructions: [[], ArrayOf(String)]
};
const ASSIGNMENT_DEFAULTS = Object.fromEntries(Object.entries(ASSIGNMENT_DEFAULTS_CONFIG).map(([k, [v]]) => [k, v]));
const Assignment = forwardRef(({
  defaults = {},
  initVals = {},
  rerender
}, ref) => {
  const defaultValues = {
    ...ASSIGNMENT_DEFAULTS,
    ...defaults
  };
  const data = {};
  Object.entries(defaultValues).forEach(([k, v]) => {
    const [val, setVal] = useState(initVals[k] ?? v);
    Object.defineProperty(data, k, {
      enumerable: true,
      get: () => val,
      set: setVal
    });
  });
  const [createAssignment, mutationState] = useMutation(CREATE_ASSIGNMENT);
  const resultError = mutationState.error ?? mutationState.data?.error ?? mutationState.data?.assignmentCreateOne?.error;
  const actions = {
    unsubmit: () => mutationState.reset(),
    reset: () => {
      unsubmit();
      Object.keys(defaultValues).forEach(k => data[k] = defaultValues[k]);
    },
    submit: (classID, studentIDs) => createAssignment({
      variables: {
        classID: classID,
        assignedTo: studentIDs,
        label: data.label,
        startDate: data.startDate?.format('YYYY-MM-DD'),
        endDate: data.endDate?.format('YYYY-MM-DD'),
        tasks: data.tasks.map(({
          contentID,
          language
        }, index) => ({
          contentID: contentID,
          language: language,
          instructions: data.instructions[index],
          order: index + 1
        }))
      }
    }),
    serialize: () => {
      const doc = new YAML.Document();
      const tasksNode = doc.createNode(data.tasks.map(t => `${t.contentID}:${t.language}`));
      tasksNode.items.forEach((subNode, i) => subNode.comment = data.tasks[i].name);
      doc.contents = {
        ...data,
        tasks: tasksNode
      };
      return doc;
    }
  };
  const status = {
    submitted: mutationState.called,
    persisted: mutationState.called && !mutationState.loading && !resultError,
    loading: mutationState.loading,
    error: resultError
  };
  useEffect(rerender, [...Object.values(data), ...Object.values(status)]);
  useImperativeHandle(ref, () => ({
    data,
    actions,
    status
  }));
  return null;
});
const formatError = e => {
  if (!e) {
    return null;
  }
  if (e.__typename !== 'ValidationError') {
    return e.message;
  }
  return <>
			<h3 children="Assignment validation failed:" />
			<ul style={{
      listStyle: 'inside none',
      margin: 'auto',
      padding: 0
    }} children={e.errors.map((e, i) => <li key={i} children={e.message} />)} />
		</>;
};
const Assignments = ({
  data: modulesIndex,
  defaults = {},
  assignmentDefaults = {},
  filters = {},
  children
}) => {
  const apolloClient = useApolloClient();
  const sanitizerAddons = {
    apolloClient,
    modulesIndex
  };
  const {
    user
  } = useSession();
  const rerender = useRerender();
  const defaultValues = {
    ...PLAYLIST_DEFAULTS,
    ...defaults
  };
  const [processing, setProcesing] = useState(false);
  const [listPrepared, setListPrepared] = useState(false);
  const assignmentData = {};
  Object.entries(defaultValues).forEach(([k, v]) => {
    const [val, setVal] = useState(v);
    Object.defineProperty(assignmentData, k, {
      enumerable: true,
      get: () => val,
      set: setVal
    });
  });
  const buildRefs = (initValsArray = [{}]) => initValsArray.map(initVals => ({
    initVals: initVals,
    key: `${Date.now()}-${Math.random()}`,
    ref: new Proxy(createRef(), {
      set: (...args) => {
        rerender();
        return Reflect.set(...args);
      }
    })
  }));
  const [assignmentRefs, setAssignmentRefs] = useState([]);
  Object.defineProperty(assignmentData, 'assignments', {
    enumerable: true,
    value: assignmentRefs.map(({
      ref
    }) => ref.current).filter(Boolean)
  });
  const [createPlaylist, mutationState] = useMutation(CREATE_ASSIGNMENT_PLAYLIST);
  const resultError = mutationState.error?.message || mutationState.data?.error?.message;
  const persisted = mutationState.called && !mutationState.loading && !resultError;
  const serializeYAML = () => {
    const doc = new YAML.Document();
    const entitiesNode = doc.createNode(assignmentData.entities.map(e => [`${e.__typename}#${e.id}`, e.type].filter(Boolean).join(':')));
    entitiesNode.items.forEach((subNode, i) => subNode.comment = assignmentData.entities[i].name);
    doc.contents = {
      ...assignmentData,
      entities: entitiesNode,
      assignments: assignmentRefs.map(({
        ref
      }) => ref.current?.actions.serialize()).filter(Boolean)
    };
    return doc.toString();
  };
  const deserializeYAML = async yamlString => {
    const uploadedData = YAML.parse(yamlString);
    for (const [key, [, sanitizer]] of Object.entries(PLAYLIST_DEFAULTS_CONFIG)) {
      assignmentData[key] = (await sanitizer(uploadedData[key], sanitizerAddons)) ?? defaultValues[key];
    }
    ;
    const assignmentDefaultVals = {
      ...ASSIGNMENT_DEFAULTS,
      ...assignmentDefaults
    };
    setAssignmentRefs(buildRefs(await Promise.all(Array.from(uploadedData.assignments).map(async uploadedAssginmentData => {
      const initVals = Object.fromEntries(await Promise.all(Object.entries(ASSIGNMENT_DEFAULTS_CONFIG).map(async ([key, [defaultVal, sanitizer]]) => [key, (await sanitizer(uploadedAssginmentData[key], sanitizerAddons)) ?? assignmentDefaultVals[key]])));
      initVals.instructions = [...initVals.instructions.slice(0, initVals.tasks.length), ...initVals.tasks.slice(initVals.instructions.length).map(t => t.instructions)];
      return initVals;
    }))));
  };
  const actions = {
    setListPrepared: setListPrepared,
    reset: () => {
      Object.keys(defaultValues).forEach(k => assignmentData[k] = defaultValues[k]);
      setAssignmentRefs([]);
      setListPrepared(false);
      mutationState.reset();
    },
    submit: () => createPlaylist({
      variables: {
        yml: btoa(serializeYAML()),
        assignedTo: {
          [`${assignmentData.entities[0].__typename.toLowerCase()}IDs`]: assignmentData.entities.map(e => e.id),
          enrollmentType: assignmentData.entities[0].type ?? [],
          gradeLevels: assignmentData.grades,
          ageGroups: assignmentData.ages
        }
      }
    }),
    unsubmit: () => {
      ['klass', 'studentIDs'].forEach(k => assignmentData[k] = defaultValues[k]);
      assignmentRefs.forEach(({
        ref
      }) => ref.current?.actions.unsubmit());
      setListPrepared(false);
      mutationState.reset();
    },
    addAssignments: (initValsArray = [{}]) => setAssignmentRefs(prevRefs => [...prevRefs, ...buildRefs(initValsArray)]),
    removeAssignments: indexes => setAssignmentRefs(prevRefs => prevRefs.filter((_, index) => !indexes.includes(index))),
    reorderAssignments: (dragIndex, dropIndex) => {
      let moved = assignmentRefs[dragIndex];
      let tmpArray = assignmentRefs.filter((_, i) => i !== dragIndex);
      tmpArray.splice(dropIndex, 0, moved);
      setAssignmentRefs(tmpArray);
    },
    serialize: serializeYAML,
    deserialize: (yamlString, msg) => {
      setProcesing(msg);
      return deserializeYAML(yamlString).finally(() => setProcesing(false));
    },
    store: () => {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify({
          userID: user.id,
          data: actions.serialize()
        }));
      } catch (e) {
        console.warn('Error on saving assignments to browser memory', e);
      }
    },
    restore: () => {
      const {
        userID,
        data
      } = JSON.parse(window.sessionStorage.getItem(STORE_KEY) ?? '{}');
      if (userID === user.id && data) {
        actions.deserialize(data, 'Restoring playlist from browser memory');
      }
    }
  };
  useEffect(actions.restore, []);
  useEffect(actions.store);
  useEffect(actions.reset, [...Object.values(defaults), ...Object.values(assignmentDefaults)]);
  useEffect(() => {
    assignmentData.grades = assignmentData.ages = [];
  }, [assignmentData.entities[0]?.type]);
  const assignmentStatuses = assignmentRefs.map(({
    ref
  }) => ref.current?.status).filter(Boolean);
  const status = {
    listPrepared: listPrepared,
    submitted: mutationState.called || assignmentStatuses.length > 0 && assignmentStatuses.some(status => status.submitted),
    persisted: persisted || assignmentStatuses.length > 0 && assignmentStatuses.every(status => status.persisted),
    loading: mutationState.loading || assignmentStatuses.length > 0 && assignmentStatuses.some(status => status.loading),
    error: !!resultError || assignmentStatuses.length > 0 && assignmentStatuses.some(status => status.error)
  };
  const ctx = Object.seal({
    data: Object.seal(assignmentData),
    actions: Object.freeze(actions),
    status: Object.freeze(status),
    filters: filters
  });
  return <AssignmentsContextProvider value={ctx} data-sentry-element="AssignmentsContextProvider" data-sentry-component="Assignments" data-sentry-source-file="Assignment.jsx">
			{assignmentRefs.map(props => <Assignment {...props} defaults={assignmentDefaults} rerender={rerender} />)}
			{children}
			{status.submitted && <Modal open closable={!status.loading && assignmentRefs.length === 1} maskClosable={!status.loading && assignmentRefs.length === 1} destroyOnClose footer={null} width={800 + 100 + 24 * 2} onCancel={actions.reset}>
						<Result icon={status.loading ? <Spin size="large" /> : undefined} status={status.error ? "error" : status.persisted ? "success" : "info"} subTitle={assignmentRefs.length === 1 ? formatError(assignmentRefs[0].ref.current.status.error) : null} title={status.loading ? mutationState.called ? `Creating playlist with ${assignmentRefs.length} assignment${assignmentRefs.length === 1 ? '' : 's'}...` : 'Creating assignment...' : `Assignment${mutationState.called ? 's playlist' : ''} ${status.error ? 'NOT' : 'successfully'} created`} extra={status.persisted && (mutationState.called ? [<Button key="close-btn" onClick={actions.unsubmit}>Close</Button>, <Button key="reset-btn" danger onClick={actions.reset}>Create Another</Button>] : [<Button key="btn" onClick={actions.reset}>Create Another Assignment</Button>])}>
						{status.persisted && !mutationState.called && <div className="assign-class">
									<CheckCircleFilled width="1.2em" height="1.2em" fill="#52C41A" />
									{' '}
									<span>{assignmentData.klass.name} ({assignmentData.klass.semester}, {assignmentData.klass.grade}) for {assignmentData.studentIDs.length} student(s)</span>
									<Button type="link">
										<AC.ButtonLink href={{
              pathname: '/reports/class/assignments-gradebook',
              query: {
                id: assignmentData.klass.id
              }
            }} onClick={() => {
              actions.reset();
              return true;
            }} type="link" label="View Class Assignments" />
									</Button>
								</div>}
						</Result>
					</Modal>}
			{processing && <Modal open closable={false} footer={null}>
						<Result icon={<Spin size="large" />} title={processing} />
					</Modal>}
		</AssignmentsContextProvider>;
};
export default cmsFactory(Assignments, '/content/modules-index.json');